import { ICoreContext } from '@msdyn365-commerce/core';
import React from 'react';
import { IOrderDetailResponse } from '../../../actions/order-details/get-order-details.action';
import DataTitle from '../../../components/data-title';
import { NO_DATA_FALLBACK, TOTAL_PRICE_DEFAULT } from '../../../configuration';
import { ILocalizationState } from '../../../states/localization-state';
import {
    convertHtmlToPlainText,
    convertSapToHtmlLinebreaks,
    dateLocaleFormatter,
    formatCurrency,
    formatNumberLocaleStatic,
    formatOverallStatusCodeForTooltip,
    formatStatusCodeForTooltip,
    mapOverallDeliveryStatus,
    mapOverallDeliveryStatusCodeDescriptionToText,
    mapOverallInvoiceStatus,
    toLocalDate
} from '../../../utilities';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';

export default function generalInformationSection({
    context,
    orderDetails,
    resources,
    localizationState
}: {
    context: ICoreContext;
    orderDetails: IOrderDetailResponse;
    resources: IOrderDetailsResources;
    localizationState: ILocalizationState;
}) {
    if (!orderDetails) {
        return <p>{resources.orderDetailsLoadingLabel}</p>;
    }

    const isEmptyPrice = (price: string) => {
        // eslint-disable-next-line security/detect-non-literal-regexp
        const regex = new RegExp(`\\b${TOTAL_PRICE_DEFAULT}\\b`);

        return regex.test(price);
    };

    return (
        <>
            <div className='row'>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralTableOrderNumber}</strong>
                    <p>{orderDetails.OrderDetail?.OrderNumber}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralTableOrderType}</strong>
                    <p>{orderDetails.OrderDetail?.OrderType}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>
                        <span>
                            {resources.orderDetailsGeneralTableOverallDelivery}{' '}
                            <DataTitle
                                label={<span className='lxs-data-title__info-icon'></span>}
                                title={formatStatusCodeForTooltip(resources, context)}
                            />
                        </span>
                    </strong>
                    <p>
                        <DataTitle
                            label={mapOverallDeliveryStatus(resources).getText(orderDetails.OrderDetail?.StateLogistics)}
                            title={mapOverallDeliveryStatusCodeDescriptionToText(orderDetails.OrderDetail.StateLogistics, resources)}
                        />
                    </p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>
                        <span>
                            {resources.orderDetailsGeneralTableOverallStatusInvoice}{' '}
                            <DataTitle
                                label={<span className='lxs-data-title__info-icon'></span>}
                                title={formatOverallStatusCodeForTooltip(resources, context)}
                            />
                        </span>
                    </strong>
                    <p>
                        <DataTitle
                            label={mapOverallInvoiceStatus(resources).getText(orderDetails.OrderDetail?.StateInvoice)}
                            title={mapOverallDeliveryStatusCodeDescriptionToText(orderDetails.OrderDetail.StateInvoice, resources)}
                        />
                    </p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>
                        {resources.orderDetailsGeneralTableOrderInformationRecipient}
                    </strong>
                    <p className='ms-order-details__text-overflow-ellipses' title={orderDetails.OrderDetail?.Recipient}>
                        {orderDetails.OrderDetail?.Recipient}
                    </p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralTableCustomerPo}</strong>
                    <p className='ms-order-details__text-customer-po'>{orderDetails.OrderDetail?.PoNumber}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralCustomerReferenceDate}</strong>
                    <p>
                        {orderDetails.OrderDetail?.ConfirmedDate &&
                            dateLocaleFormatter(localizationState).formatDate(toLocalDate(orderDetails.OrderDetail.ConfirmedDate))}
                    </p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralTableTotalWeight}</strong>
                    <p>{`${formatNumberLocaleStatic(Number(orderDetails.OrderDetail.TotalWeight), localizationState)} ${
                        orderDetails.OrderDetail.Unit
                    }`}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>
                        <span>
                            {resources.orderDetailsGeneralTableTotalPrice}{' '}
                            <DataTitle
                                label={<span className='lxs-data-title__info-icon'></span>}
                                title={resources.orderDetailsGeneralTableTotalPriceToolTip}
                            />
                        </span>
                    </strong>
                    <p className='ms-order-details__block-item'>
                        {isEmptyPrice(orderDetails.OrderDetail?.TotalPrice) || !Number(orderDetails.OrderDetail?.TotalPrice)
                            ? NO_DATA_FALLBACK
                            : formatCurrency(
                                  localizationState,
                                  orderDetails.OrderDetail?.Currency,
                                  Number(orderDetails.OrderDetail?.TotalPrice)
                              )}
                    </p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.createOrderSalesOrgLabel}</strong>
                    <p>{orderDetails.OrderDetail?.SalesOrganisationText}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralIncoterm1}</strong>
                    <p>{orderDetails.OrderDetail?.Incoterm1}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralIncoterm2}</strong>
                    <p>{orderDetails.OrderDetail?.Incoterm2}</p>
                </div>
                <div className='col-md-3 col-sm-6'>
                    <strong className='ms-order-details__text-auto-hyphens'>{resources.orderDetailsGeneralTablePaymentTerms}</strong>
                    <p>{orderDetails.OrderDetail?.PaymentTerms}</p>
                </div>
            </div>
            <h4 className='ms-order-details__section-heading'>{resources.orderDetailsAdditionalInformationSectionHeading}</h4>
            <div className='row'>
                <div className='col-md-12'>
                    {orderDetails.OrderDetail?.HeaderTexts && Object.keys(orderDetails.OrderDetail?.HeaderTexts).length ? (
                        <p
                            className='lxs-create-order__text-auto-hyphens'
                            dangerouslySetInnerHTML={{
                                __html: convertSapToHtmlLinebreaks(
                                    convertHtmlToPlainText(orderDetails.OrderDetail?.HeaderTexts[0].TextValue)
                                )
                            }}
                        />
                    ) : (
                        <p>{NO_DATA_FALLBACK}</p>
                    )}
                </div>
            </div>
        </>
    );
}
